"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

/** 仅结构数据：图片与外链；标题与描述从 translationNamespace 读取 service1Title…service6Desc */
export interface PrecisionServiceBlock {
	image: string;
	learnMoreHref?: string | null;
}

export interface SheetMetalPrecisionServicesProps {
	/** next-intl 命名空间 */
	translationNamespace?: string;
	/** 服务卡片顺序与命名空间中 service1…service6 一一对应 */
	serviceBlocks: readonly PrecisionServiceBlock[];
	/** Get a Quote 按钮跳转链接 */
	getQuoteHref?: string;
}

const DEFAULT_GET_QUOTE_HREF = "/upload";

/** 与 en.json 中 service1Title…service6Title 顺序一致 */
const SERVICE_TITLE_KEYS = [
	"service1Title",
	"service2Title",
	"service3Title",
	"service4Title",
	"service5Title",
	"service6Title",
] as const;
const SERVICE_DESC_KEYS = [
	"service1Desc",
	"service2Desc",
	"service3Desc",
	"service4Desc",
	"service5Desc",
	"service6Desc",
] as const;

/**
 * Sheet Metal Fabrication 服务卡片网格：3x2，文案来自翻译文件
 */
export default function SheetMetalPrecisionServices({
	translationNamespace = "sheetMetalFabricationPrecisionServices",
	serviceBlocks,
	getQuoteHref = DEFAULT_GET_QUOTE_HREF,
}: SheetMetalPrecisionServicesProps) {
	const t = useTranslations(translationNamespace);

	return (
		<section className="bg-white py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<div className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="mb-3 text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
						{t("title")}
					</h2>
					<p className="text-sm leading-relaxed text-black md:text-base">{t("description")}</p>
				</div>

				<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{serviceBlocks.map((block, index) => {
						const n = index + 1;
						const title = t(SERVICE_TITLE_KEYS[index]);
						const description = t(SERVICE_DESC_KEYS[index]);
						return (
							<article
								key={`sheet-metal-service-${n}`}
								className="flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
							>
								<div className="relative aspect-[16/10] w-full overflow-hidden">
									<Image
										src={block.image}
										alt={title}
										fill
										className="object-cover"
										sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
									/>
								</div>
								<div className="flex flex-1 flex-col px-5 py-5">
									<h3 className="mb-3 text-lg font-bold text-black sm:text-xl md:text-2xl">{title}</h3>
									<p className="mb-5 flex-1 text-sm leading-relaxed text-black md:text-base">{description}</p>
									<div className="flex items-center justify-between gap-3">
										<a
											href={getQuoteHref}
											target="_blank"
											rel="noopener noreferrer"
											className="inline-flex items-center justify-center rounded-lg bg-themePrimary400 px-4 py-2.5 text-sm font-bold text-white transition-all duration-300 hover:bg-opacity-80 hover:text-white sm:px-5 sm:py-3"
										>
											{t("getQuote")}
										</a>
										{block.learnMoreHref && (
											<a
												href={block.learnMoreHref}
												target="_blank"
												rel="noopener noreferrer"
												className="flex items-center gap-1 text-sm font-bold text-themePrimary400 transition-colors duration-300 hover:underline"
											>
												{t("learnMore")}
												<svg
													className="h-4 w-4"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
													aria-hidden="true"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
													/>
												</svg>
											</a>
										)}
									</div>
								</div>
							</article>
						);
					})}
				</div>
			</div>
		</section>
	);
}
